import type {
  BrandSiteCollection,
  BrandSiteItemSchema,
  BrandSiteItemSchemaField,
} from "@/lib/brand-site-api";

/** 表单控件类型（一期：image/url 仍用文本框） */
export type ItemSchemaFormControl = "text" | "textarea" | "url";

export type ItemSchemaFormField = {
  id: string;
  label: string;
  control: ItemSchemaFormControl;
  /** schema 原始 type，便于调试 */
  schemaType: string;
};

const TEXTAREA_TYPES = new Set(["html", "richtext", "textarea", "body"]);
const URL_TYPES = new Set(["image", "url", "cover"]);

type LooseSchemaField = {
  id?: unknown;
  name?: unknown;
  key?: unknown;
  type?: unknown;
  label?: unknown;
  default?: unknown;
};

/** 字段键：优先 id，兼容误用的 name / key（与 schema 组名 name 无关） */
export function resolveSchemaFieldId(field: LooseSchemaField | null | undefined): string {
  if (!field || typeof field !== "object") return "";
  for (const candidate of [field.id, field.name, field.key]) {
    if (typeof candidate === "string" && candidate.trim()) return candidate.trim();
  }
  return "";
}

/** 栏目设置 UI 用的 type：把 richtext/url 收成可选值 */
export function normalizeSchemaFieldTypeForEditor(type: string | undefined): string {
  const t = (type || "text").trim().toLowerCase() || "text";
  if (t === "richtext" || t === "body") return "html";
  if (t === "url" || t === "cover") return "image";
  return t;
}

/** 把 settings 项归一成带 id 的字段；无可用键则丢弃 */
export function normalizeSchemaField(
  field: LooseSchemaField | null | undefined,
): BrandSiteItemSchemaField | null {
  const id = resolveSchemaFieldId(field);
  if (!id) return null;
  const type =
    typeof field?.type === "string" && field.type.trim() ? field.type.trim() : "text";
  const label =
    typeof field?.label === "string" && field.label.trim() ? field.label.trim() : id;
  const normalized: BrandSiteItemSchemaField = { id, type, label };
  if (typeof field?.default === "string") normalized.default = field.default;
  return normalized;
}

/** 归一化整份 item schema（解析 / API 下发后共用） */
export function normalizeItemSchema(
  schema: BrandSiteItemSchema | null | undefined,
): BrandSiteItemSchema {
  const settings = Array.isArray(schema?.settings)
    ? schema.settings
        .map((field) => normalizeSchemaField(field))
        .filter((field): field is BrandSiteItemSchemaField => field != null)
    : [];
  const name =
    typeof schema?.name === "string" && schema.name.trim() ? schema.name.trim() : undefined;
  return name ? { name, settings } : { settings };
}

/**
 * 将 Liquid schema settings 映射为表单字段。
 * 无 settings 时回退常见字段 title / body / cover。
 */
export function schemaToFormFields(schema: BrandSiteItemSchema | null | undefined): ItemSchemaFormField[] {
  const settings = normalizeItemSchema(schema).settings;
  if (Array.isArray(settings) && settings.length > 0) {
    return settings.map((field) => ({
      id: field.id,
      label: field.label?.trim() || field.id,
      control: controlForType(field.type, field.id),
      schemaType: field.type || "text",
    }));
  }

  return [
    { id: "title", label: "标题", control: "text", schemaType: "text" },
    { id: "cover", label: "封面", control: "url", schemaType: "image" },
    { id: "body", label: "正文", control: "textarea", schemaType: "html" },
  ];
}

function controlForType(type: string | undefined, id: string): ItemSchemaFormControl {
  const t = (type || "").toLowerCase();
  const key = id.toLowerCase();
  if (TEXTAREA_TYPES.has(t) || key === "body") return "textarea";
  if (URL_TYPES.has(t) || key === "cover") return "url";
  return "text";
}

/** 从表单值拼 settings；空封面等存 ""，与 Liquid blank 行为一致 */
export function formValuesToSettings(
  fields: ItemSchemaFormField[],
  values: Record<string, string>,
): Record<string, string> {
  const settings: Record<string, string> = {};
  for (const field of fields) {
    settings[field.id] = values[field.id] ?? "";
  }
  return settings;
}

/** 从条目 settings 填表；缺省为空串 */
export function settingsToFormValues(
  fields: ItemSchemaFormField[],
  settings: Record<string, unknown> | null | undefined,
): Record<string, string> {
  const source = settings ?? {};
  const values: Record<string, string> = {};
  for (const field of fields) {
    const raw = source[field.id];
    values[field.id] = raw == null ? "" : String(raw);
  }
  return values;
}

/** 极简解析 {% schema %}…{% endschema %}（无服务端 item_schema 时兜底） */
export function parseItemSchemaFromTemplate(html: string): BrandSiteItemSchema {
  const match = html.match(/\{%\s*schema\s*%\}([\s\S]*?)\{%\s*endschema\s*%\}/m);
  if (!match?.[1]) return { settings: [] };
  try {
    const parsed = JSON.parse(match[1].trim()) as BrandSiteItemSchema;
    return parsed && typeof parsed === "object" ? normalizeItemSchema(parsed) : { settings: [] };
  } catch {
    return { settings: [] };
  }
}

const SCHEMA_BLOCK_RE = /\{%\s*schema\s*%\}[\s\S]*?\{%\s*endschema\s*%\}/m;

/** 把 schema 写回单篇模板；已有 block 则替换，否则追加到末尾 */
export function upsertSchemaInTemplate(
  html: string,
  schema: BrandSiteItemSchema,
): string {
  const payload: BrandSiteItemSchema = {
    name: schema.name?.trim() || "文章",
    settings: Array.isArray(schema.settings) ? schema.settings : [],
  };
  const block = `{% schema %}\n${JSON.stringify(payload, null, 2)}\n{% endschema %}`;
  const source = html ?? "";
  if (SCHEMA_BLOCK_RE.test(source)) {
    return source.replace(SCHEMA_BLOCK_RE, block);
  }
  const trimmed = source.trimEnd();
  return trimmed ? `${trimmed}\n\n${block}\n` : `${block}\n`;
}

/** 规范化字段表（去空 id、默认 type） */
export function normalizeSchemaSettings(
  rows: Array<{ id?: string | null; type?: string | null; label?: string | null }>,
): BrandSiteItemSchemaField[] {
  const seen = new Set<string>();
  const out: BrandSiteItemSchemaField[] = [];
  for (const row of rows) {
    const id = String(row.id ?? "")
      .trim()
      .replace(/\s+/g, "_");
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push({
      id,
      type: String(row.type ?? "").trim() || "text",
      label: String(row.label ?? "").trim() || id,
    });
  }
  return out;
}

/** 从栏目模板 / item_schema 抽出设置表行（兼容 settings[].name 当键） */
export function schemaSettingsToEditorRows(
  collection: Pick<BrandSiteCollection, "item_template_html" | "item_schema">,
): { schemaName: string; rows: Array<{ id: string; type: string; label: string }> } {
  const fromTemplate = parseItemSchemaFromTemplate(collection.item_template_html ?? "");
  const schema = normalizeItemSchema(
    fromTemplate.settings && fromTemplate.settings.length > 0
      ? fromTemplate
      : (collection.item_schema ?? fromTemplate),
  );
  return {
    schemaName: schema.name?.trim() || "文章",
    rows: (schema.settings ?? []).map((field) => ({
      id: field.id,
      type: normalizeSchemaFieldTypeForEditor(field.type),
      label: field.label?.trim() || field.id,
    })),
  };
}

/** 是否走正文三模编辑器（HTML / Markdown / 预览·AI） */
export function isHtmlItemSchemaField(field: ItemSchemaFormField): boolean {
  const t = field.schemaType.toLowerCase();
  return t === "html" || t === "richtext" || t === "body" || field.id.toLowerCase() === "body";
}
